import { Badge, Button, Card, Flex, Heading, Tabs, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useRef } from 'react';
import {
	ConfirmModal,
	createDataCollectionContext,
	dbTimeToUserTime,
	toaster,
	useMobileContext,
	getBadgeColorByStatus,
	Tooltip,
	type ConfirmModalHandle,
	type ItemAction,
	useValidatedSorting,
	parseErrorToString,
} from '@shared';
import { SmsMessagesService, useDeleteMultipleSmsMessages, useGetAllSmsMessages } from '@admin';
import { useNavigate, useSearchParams } from 'react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { useQueryClient } from '@tanstack/react-query';
import { useMeta } from '../../api/hooks/useMeta';
import { IoCheckmarkCircleOutline, IoCloseCircleOutline, IoSyncCircleOutline } from 'react-icons/io5';
import InboundMessages from './InboundMessages';
import { Helmet } from 'react-helmet';
import axiosInstance from '../../api/axiosInstance';

const columnHelper = createColumnHelper<SmsMessage>();
const TIMEZONE = import.meta.env.VITE_DEFAULT_TIMEZONE as string;

export default function MessageHistory({ isTopCardShown = true }: { isTopCardShown?: boolean }) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { isMobile } = useMobileContext();
	const { meta } = useMeta('smsMessagesService');

	const { data: apiSmsMessages } = useGetAllSmsMessages({
		filters: `sendingStatus:ne:draft|createdFrom:eq:api`,
		itemsPerPage: '1',
	});

	const { DataCollectionContextProvider, DataCollectionTable, DataCollectionTableFooter, DataCollectionTableHeader } = useMemo(
		() => createDataCollectionContext<SmsMessage>(),
		[],
	);

	const [searchParams, setSearchParams] = useSearchParams();

	const defaultTab = 'messageHistory';
	const currentTab = searchParams.get('tab') || defaultTab;

	useEffect(() => {
		if (!searchParams.get('tab')) {
			setSearchParams({ tab: defaultTab }, { replace: true });
		}
	}, [searchParams, setSearchParams]);

	const resendModal = useRef<ConfirmModalHandle>(null);

	async function handleResendMessage(message: SmsMessage): Promise<void> {
		const response = await resendModal.current?.open({
			title: t('general.resend_message'),
			content: t('general.really_resend_message'),
			confirmButtonText: t('general.yes'),
		});

		if (response === false) {
			return;
		}

		try {
			await axiosInstance.post('app/smsMessages/resend', { smsMessageID: message.smsMessageID });
			toaster.create({
				type: 'success',
				title: t('general.message_sent'),
			});
		} catch (error) {
			toaster.create({
				type: 'error',
				title: parseErrorToString(error),
			});
		}
	}

	const columns = [
		columnHelper.accessor('name', {
			header: t('general.name'),
			sortingFn: 'basic',
		}),

		columnHelper.accessor('sender', {
			header: t('general.sender'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('smsMessageRecipientsCount', {
			header: t('general.receivers'),
			enableSorting: false,
			cell: ({ row }) => {
				if (row.original.soleRecipient) {
					return <Text>{row.original.soleRecipient.phone}</Text>;
				}
				return <Text>{row.original.smsMessageRecipientsCount || row.original.expectedRecipientCount}</Text>;
			},
		}),

		columnHelper.display({
			id: 'deliveryDetails',
			header: t('messages.deliver_details'),
			cell: ({ row }) => {
				const deliveredCount = row.original.deliveredCount ?? 0;
				const processingCount = row.original.processingCount ?? 0;
				const failedCount = row.original.failedCount ?? 0;

				if (!row.original.timeSent) return t('general.not_sent');

				return (
					<Flex gap={1}>
						<Tooltip openDelay={150} closeDelay={100} content={t('general.successful_sends')}>
							<Badge minW="45px" colorPalette="green" className="group">
								<IoCheckmarkCircleOutline size={18} />
								{deliveredCount}
							</Badge>
						</Tooltip>

						{processingCount > 0 && (
							<Tooltip openDelay={150} closeDelay={100} content={t('messages.sending_in_progress')}>
								<Badge minW="45px" colorPalette="blue">
									<IoSyncCircleOutline size={18} />
									{processingCount}
								</Badge>
							</Tooltip>
						)}
						{failedCount > 0 && (
							<Tooltip openDelay={150} closeDelay={100} content={t('messages.sending_failed')}>
								<Badge minW="45px" colorPalette="red">
									<IoCloseCircleOutline size={18} />
									{failedCount}
								</Badge>
							</Tooltip>
						)}
					</Flex>
				);
			},
		}),
		columnHelper.accessor('creditCount', {
			header: t('general.used_credits'),
			enableSorting: false,
		}),
		columnHelper.accessor('sendingStatus', {
			header: t('general.status'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return <Badge colorPalette={getBadgeColorByStatus(row.original.sendingStatus)}>{meta?.sendingStatus?.[row.original.sendingStatus]}</Badge>;
			},
			filterFn: 'arrIncludesSomeSensitive',
			meta: {
				filterVariant: 'select',
				filterOptions: [
					{ value: 'completed', label: meta?.sendingStatus?.['completed'] ?? '' },
					{ value: 'error', label: meta?.sendingStatus?.['error'] ?? '' },
					{ value: 'ongoing', label: meta?.sendingStatus?.['ongoing'] ?? '' },
					{ value: 'scheduled', label: meta?.sendingStatus?.['scheduled'] ?? '' },
					{ value: 'waitingToBeSent', label: meta?.sendingStatus?.['waitingToBeSent'] ?? '' },
				] as Array<{ value: SendingStatus; label: string }>,
			},
		}),
		columnHelper.accessor('timeSent', {
			header: t('general.time_sent'),
			sortingFn: 'basic',
			filterFn: 'dateTimeBetween',
			meta: {
				filterVariant: 'dateTime',
				filterLabel: [t('messages.time_sent_after'), t('messages.time_sent_before')],
			},
			cell: ({ row }) => {
				if (row.original.timeSent) {
					return <Text>{dbTimeToUserTime(row.original.timeSent, TIMEZONE)}</Text>;
				}
				return t('general.not_sent');
			},
		}),
		columnHelper.accessor('timeScheduledSend', {
			header: t('general.time_scheduled'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (row.original.timeScheduledSend) {
					return <Text>{dbTimeToUserTime(row.original.timeScheduledSend, TIMEZONE)}</Text>;
				}
				return t('general.not_scheduled');
			},
		}),
		columnHelper.accessor('timeCreated', {
			header: t('general.time_created'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return <Text>{dbTimeToUserTime(row.original.timeCreated, TIMEZONE)}</Text>;
			},
		}),
		columnHelper.display({
			id: 'smsMessageID',
			cell: ({ row }) => {
				return (
					<Flex width={'100%'} justifyContent={'end'} gap="5px">
						{row.original.sendingStatus === 'error' ? (
							<Button
								data-cy="resend"
								variant={'outline'}
								onClick={e => {
									e.stopPropagation();
									handleResendMessage(row.original);
								}}
								size="xs"
							>
								{t('general.resend')}
							</Button>
						) : null}
						<Button
							data-cy="useTemplate"
							onClick={e => {
								e.stopPropagation();
								handleUseTemplate(row.original.smsMessageID, row.original.name);
							}}
							size="xs"
						>
							{t('general.use_as_template')}
						</Button>
					</Flex>
				);
			},
		}),
	];

	const columnsApi = [
		columnHelper.accessor('name', {
			header: t('general.name'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('externalID', {
			header: t('general.external_id'),
			enableSorting: false,
		}),
		columnHelper.accessor('sender', {
			header: t('general.sender'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('smsMessageRecipientsCount', {
			header: t('general.receivers'),
			enableSorting: false,
			cell: ({ row }) => {
				if (row.original.soleRecipient) {
					return <Text>{row.original.soleRecipient.phone}</Text>;
				}
				return <Text>{row.original.smsMessageRecipientsCount || row.original.expectedRecipientCount}</Text>;
			},
		}),

		columnHelper.display({
			id: 'deliveryDetails',
			header: t('messages.deliver_details'),
			cell: ({ row }) => {
				const deliveredCount = row.original.deliveredCount ?? 0;
				const processingCount = row.original.processingCount ?? 0;
				const failedCount = row.original.failedCount ?? 0;

				if (!row.original.timeSent) return t('general.not_sent');

				return (
					<Flex gap={1}>
						<Tooltip openDelay={150} closeDelay={100} content={t('general.successful_sends')}>
							<Badge minW="45px" colorPalette="green" className="group">
								<IoCheckmarkCircleOutline size={18} />
								{deliveredCount}
							</Badge>
						</Tooltip>

						{processingCount > 0 && (
							<Tooltip openDelay={150} closeDelay={100} content={t('messages.sending_in_progress')}>
								<Badge minW="45px" colorPalette="blue">
									<IoSyncCircleOutline size={18} />
									{processingCount}
								</Badge>
							</Tooltip>
						)}
						{failedCount > 0 && (
							<Tooltip openDelay={150} closeDelay={100} content={t('messages.sending_failed')}>
								<Badge minW="45px" colorPalette="red">
									<IoCloseCircleOutline size={18} />
									{failedCount}
								</Badge>
							</Tooltip>
						)}
					</Flex>
				);
			},
		}),
		columnHelper.accessor('creditCount', {
			header: t('general.used_credits'),
			enableSorting: false,
		}),
		columnHelper.accessor('sendingStatus', {
			header: t('general.status'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return <Badge colorPalette={getBadgeColorByStatus(row.original.sendingStatus)}>{meta?.sendingStatus?.[row.original.sendingStatus]}</Badge>;
			},
			filterFn: 'arrIncludesSomeSensitive',
			meta: {
				filterVariant: 'select',
				filterOptions: [
					{ value: 'completed', label: meta?.sendingStatus?.['completed'] ?? '' },
					{ value: 'error', label: meta?.sendingStatus?.['error'] ?? '' },
					{ value: 'ongoing', label: meta?.sendingStatus?.['ongoing'] ?? '' },
					{ value: 'scheduled', label: meta?.sendingStatus?.['scheduled'] ?? '' },
					{ value: 'waitingToBeSent', label: meta?.sendingStatus?.['waitingToBeSent'] ?? '' },
				] as Array<{ value: SendingStatus; label: string }>,
			},
		}),
		columnHelper.accessor('timeSent', {
			header: t('general.time_sent'),
			sortingFn: 'basic',
			filterFn: 'dateTimeBetween',
			meta: {
				filterVariant: 'dateTime',
				filterLabel: [t('messages.time_sent_after'), t('messages.time_sent_before')],
			},
			cell: ({ row }) => {
				if (row.original.timeSent) {
					return <Text>{dbTimeToUserTime(row.original.timeSent, TIMEZONE)}</Text>;
				}
				return t('general.not_sent');
			},
		}),
		columnHelper.accessor('timeScheduledSend', {
			header: t('general.time_scheduled'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (row.original.timeScheduledSend) {
					return <Text>{dbTimeToUserTime(row.original.timeScheduledSend, TIMEZONE)}</Text>;
				}
				return t('general.not_scheduled');
			},
		}),
		columnHelper.accessor('timeCreated', {
			header: t('general.time_created'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return <Text>{dbTimeToUserTime(row.original.timeCreated, TIMEZONE)}</Text>;
			},
		}),
		columnHelper.display({
			id: 'smsMessageID',
			cell: ({ row }) => {
				return (
					<Flex width={'100%'} justifyContent={'end'} gap="5px">
						{row.original.sendingStatus === 'error' ? (
							<Button
								data-cy="resend"
								variant={'outline'}
								onClick={e => {
									e.stopPropagation();
									handleResendMessage(row.original);
								}}
								size="xs"
							>
								{t('general.resend')}
							</Button>
						) : null}
						<Button
							data-cy="useTemplate"
							onClick={e => {
								e.stopPropagation();
								handleUseTemplate(row.original.smsMessageID, row.original.name);
							}}
							size="xs"
						>
							{t('general.use_as_template')}
						</Button>
					</Flex>
				);
			},
		}),
	];

	useValidatedSorting('smsMessages', columns);

	async function handleUseTemplate(id: number, name: string): Promise<void> {
		try {
			const response = await SmsMessagesService.createMessageFromMessage(id, name);
			navigate(`/messages/drafts/${response.data.smsMessage.smsMessageID}`);
		} catch (error: unknown) {
			toaster.create({ type: 'error', title: parseErrorToString(error) });
		}
	}

	const { mutate: deleteMultipleSmsMessages } = useDeleteMultipleSmsMessages();

	const confirmRemoveRef = useRef<ConfirmModalHandle>(null);

	const selectedItemsActions: Array<ItemAction<SmsMessage>> = [
		{
			id: 'deleteMany',
			label: t('general.delete_selected'),
			callbackFn: async (ids, table) => {
				const payload = {
					smsMessages: ids.map(id => {
						return {
							smsMessageID: parseInt(id),
							delete: true,
						};
					}),
				};

				const response = await confirmRemoveRef.current?.open({
					title: t('general.remove_messages'),
					content: t('general.really_remove_messages'),
					confirmButtonText: t('general.remove'),
					confirmButtonVariant: 'subtle',
				});

				if (response === true) {
					deleteMultipleSmsMessages(payload, {
						onSuccess: () => {
							toaster.create({
								type: 'success',
								title: t('general.removed'),
							});
							queryClient.invalidateQueries({ queryKey: ['smsMessages'] });
							table.resetRowSelection();
						},
					});
				}
			},
		},
	];

	const helpTexts = [
		{
			tab: 'messageHistory',
			heading: t('general.message_history'),
			description: t('general.sent_messages_guide'),
		},
		{
			tab: 'apiMessageHistory',
			heading: t('general.api_message_history'),
			description: t('general.sent_messages_guide_api'),
		},
		{
			tab: 'inboundMessages',
			heading: t('general.inbound_messages'),
			description: t('general.inbound_messages_desc'),
		},
	];

	return (
		<>
			<Helmet>
				<meta name="robots" content="noindex, nofollow" />
			</Helmet>
			<ConfirmModal ref={resendModal} />

			<Flex flexDirection="column" gap={5} height={isTopCardShown ? '100%' : ''} minWidth={isTopCardShown || isMobile ? '100%' : '70%'} flex={1} alignItems="center">
				{!isMobile && isTopCardShown && (
					<Card.Root maxWidth="2000px" width="100%">
						<Card.Body h="full">
							<Flex h="full">
								<Flex flexDir="column" flex={1}>
									<Heading>{helpTexts.find(text => text.tab === currentTab)?.heading}</Heading>
									<Text>{helpTexts.find(text => text.tab === currentTab)?.description}</Text>
								</Flex>
							</Flex>
						</Card.Body>
					</Card.Root>
				)}
				<Tabs.Root
					maxWidth="2000px"
					variant={'line'}
					size="lg"
					colorPalette={'brand.primary'}
					value={currentTab}
					onValueChange={value => {
						setSearchParams({ tab: value.value });
					}}
					width={'100%'}
					display={'flex'}
					flexDirection={'column'}
					height={'100%'}
					lazyMount
					unmountOnExit
				>
					<Tabs.List flexWrap={'wrap'} display={isMobile ? 'contents' : 'flex'}>
						<Tabs.Trigger value="messageHistory" fontWeight={500}>
							{t('general.message_history')}
						</Tabs.Trigger>
						{apiSmsMessages?.data.smsMessages && apiSmsMessages.data.smsMessages.length > 0 ? (
							<Tabs.Trigger value="apiMessageHistory" fontWeight={500}>
								{t('general.api_message_history')}
							</Tabs.Trigger>
						) : null}
						<Tabs.Trigger value="inboundMessages" fontWeight={500}>
							{t('general.inbound_messages')}
						</Tabs.Trigger>
					</Tabs.List>

					<Tabs.Content value="messageHistory" display={'flex'} flex={1}>
						<Card.Root data-cy="messageHistoryCard" maxWidth={'2000px'} width="100%" flex={1} minWidth={'340px'}>
							<Card.Body flexWrap={'wrap'} display={'flex'} flexDirection={'row'} justifyContent={'space-between'} padding={5} gap={5}>
								<Flex display={'flex'} flexDirection={'column'} gap={3} flex={1} maxWidth={'100%'}>
									<DataCollectionContextProvider
										dataKey="smsMessages"
										keyAttribute="smsMessageID"
										emptyDataText={t('general.no_messages_found')}
										fetchFn={(...args) => SmsMessagesService.getAllResources(...args)}
										queryParams={{
											filters: `sendingStatus:ne:draft|createdFrom:eq:ui`,
											scopeWith: 'creditCount|processingCount|deliveredCount|failedCount|expectedRecipientCount|soleRecipient',
											countRelated: 'smsMessageRecipients',
										}}
										selectedItemsActions={selectedItemsActions}
										initialItemsPerPage={25}
										columns={columns}
										defaultHiddenColumns={[]}
										isServerSide={true}
										isSelectable={false}
										defaultSort={{ id: 'timeCreated', desc: true }}
										onClickRow={row => {
											navigate(`/message-history/${row.original.smsMessageID}`);
										}}
									>
										<Flex gap={2} flexWrap={'wrap'}>
											<DataCollectionTableHeader />
										</Flex>
										<DataCollectionTable />
										<Flex gap={2}>
											<DataCollectionTableFooter />
											<ConfirmModal ref={confirmRemoveRef} />
										</Flex>
									</DataCollectionContextProvider>
								</Flex>
							</Card.Body>
						</Card.Root>
					</Tabs.Content>
					<Tabs.Content value="apiMessageHistory" display={'flex'} flex={1}>
						<Card.Root data-cy="messageHistoryCard" maxWidth={'2000px'} width="100%" flex={1} minWidth={'340px'}>
							<Card.Body flexWrap={'wrap'} display={'flex'} flexDirection={'row'} justifyContent={'space-between'} padding={5} gap={5}>
								<Flex display={'flex'} flexDirection={'column'} gap={3} flex={1} maxWidth={'100%'}>
									<DataCollectionContextProvider
										dataKey="smsMessages"
										keyAttribute="smsMessageID"
										emptyDataText={t('general.no_messages_found')}
										fetchFn={(...args) => SmsMessagesService.getAllResources(...args)}
										queryParams={{
											filters: `sendingStatus:ne:draft|createdFrom:eq:api`,
											scopeWith: 'creditCount|processingCount|deliveredCount|failedCount|expectedRecipientCount|soleRecipient',
											countRelated: 'smsMessageRecipients',
										}}
										selectedItemsActions={selectedItemsActions}
										initialItemsPerPage={25}
										columns={columnsApi}
										defaultHiddenColumns={[]}
										isServerSide={true}
										isSelectable={false}
										defaultSort={{ id: 'timeCreated', desc: true }}
										onClickRow={row => {
											navigate(`/message-history/${row.original.smsMessageID}`);
										}}
									>
										<Flex gap={2} flexWrap={'wrap'}>
											<DataCollectionTableHeader />
										</Flex>
										<DataCollectionTable />
										<Flex gap={2}>
											<DataCollectionTableFooter />
											<ConfirmModal ref={confirmRemoveRef} />
										</Flex>
									</DataCollectionContextProvider>
								</Flex>
							</Card.Body>
						</Card.Root>
					</Tabs.Content>
					<Tabs.Content value="inboundMessages" display={'flex'} flex={1}>
						<InboundMessages />
					</Tabs.Content>
				</Tabs.Root>
			</Flex>
		</>
	);
}
